import React, { useState } from 'react';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import styled from 'styled-components';
import RSSFeeds from './BlogSidebar/RSSFeeds';
import AppAwareLink from './BlogSidebar/AppAwareLink';

const Sidebar = styled.aside`
  width: 100%;
  
  @media (max-width: 968px) {
    margin-top: 2rem;
  }
`;

const SidebarWidget = styled.div`
  margin-bottom: 20px;
  padding: 22px;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const WidgetTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1.2rem;
  padding-bottom: 0.8rem;
  border-bottom: 2px solid ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.headingColor};
  font-weight: 600;
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CategoryItem = styled.li`
  margin-bottom: 0.5rem;
  
  a {
    display: block;
    padding: 8px 0;
    color: ${props => props.theme.colors.textColor};
    text-decoration: none;
    transition: color 0.2s ease;
    border-bottom: 1px solid ${props => props.theme.colors.borderColorMuted};
    font-weight: ${props => props.active ? '600' : '400'};
    color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textColor};
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const RecentPostList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const RecentPostItem = styled.li`
  margin-bottom: 1.2rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid ${props => props.theme.colors.borderColorMuted};
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const RecentPostTitle = styled.h5`
  font-size: 1rem;
  margin: 0 0 0.5rem;
  line-height: 1.4;
  
  a {
    color: ${props => props.theme.colors.textColor};
    text-decoration: none;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const RecentPostDate = styled.time`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textColor};
  opacity: 0.7;
`;

const TagsCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TagItem = styled(AppAwareLink)`
  display: inline-block;
  padding: 5px 12px;
  background-color: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.textColor};
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 999px;
  margin: 0 8px 8px 0;
  font-size: 0.85rem;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const SearchForm = styled.form`
  width: 100%;
  display: flex;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 15px;
  border-radius: 999px 0 0 999px;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-right: none;
  
  &:focus {
    outline: 3px solid ${props => props.theme.colors.focusRing};
    outline-offset: 2px;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SearchButton = styled.button`
  padding: 0 15px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 0 999px 999px 0;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primaryHover || '#0062cc'};
  }
`;

const BlogSidebar = ({ currentCategory }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get all blog posts for sidebar
  const data = useStaticQuery(graphql`
    query BlogSidebarQuery {
      allMarkdownRemark(
        sort: {fields: [frontmatter___date], order: DESC}
        limit: 10
      ) {
        edges {
          node {
            fields {
              slug
            }
            frontmatter {
              date(formatString: "DD.MMMM.YYYY")
              title
              category
              tags
            }
          }
        }
      }
    }
  `);
  
  const allPosts = data.allMarkdownRemark.edges;
  
  // Extract unique categories from all posts
  const categories = new Set();
  allPosts.forEach(({ node }) => {
    if (node.frontmatter.category) {
      categories.add(node.frontmatter.category);
    }
  });
  
  // Create array from Set and sort alphabetically
  const categoryList = Array.from(categories).sort();
  
  // Extract all tags
  const allTags = [];
  allPosts.forEach(({ node }) => {
    if (node.frontmatter.tags && Array.isArray(node.frontmatter.tags)) {
      allTags.push(...node.frontmatter.tags);
    }
  });
  
  // Count tag occurrences and get unique tags
  const tagCounts = {};
  allTags.forEach(tag => {
    tagCounts[tag] = (tagCounts[tag] || 0) + 1;
  });
  
  // Get unique tags
  const uniqueTags = Object.keys(tagCounts);
  
  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/blog/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };
  
  return (
    <Sidebar>
      <SidebarWidget>
        <WidgetTitle>Search</WidgetTitle>
        <SearchForm onSubmit={handleSearchSubmit}>
          <SearchInput 
            type="text" 
            aria-label="Search blog posts"
            placeholder="Search..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <SearchButton type="submit" aria-label="Submit blog search">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </SearchButton>
        </SearchForm>
      </SidebarWidget>
      
      <SidebarWidget>
        <WidgetTitle>Categories</WidgetTitle>
        <CategoryList>
          {categoryList.map(category => (
            <CategoryItem key={category} active={category === currentCategory}>
              <AppAwareLink to={`/blog/category/${category.toLowerCase().replace(/ /g, '-')}`}>
                {category}
              </AppAwareLink>
            </CategoryItem>
          ))}
        </CategoryList>
      </SidebarWidget>
      
      <SidebarWidget>
        <WidgetTitle>Recent Posts</WidgetTitle>
        <RecentPostList>
          {allPosts.slice(0, 5).map(({ node }) => (
            <RecentPostItem key={node.fields.slug}>
              <RecentPostDate>{node.frontmatter.date}</RecentPostDate>
              <RecentPostTitle>
                <AppAwareLink to={node.fields.slug}>
                  {node.frontmatter.title}
                </AppAwareLink>
              </RecentPostTitle>
            </RecentPostItem>
          ))}
        </RecentPostList>
      </SidebarWidget>
      
      <SidebarWidget>
        <WidgetTitle>Tags</WidgetTitle>
        <TagsCloud>
          {uniqueTags.map(tag => {
            // Properly encode the tag for URL
            const encodedTag = tag.toLowerCase()
              .replace(/ /g, '-')
              .replace(/[^\w-]/g, ''); // Remove any non-word characters (except hyphens)
            
            return (
              <TagItem 
                key={tag} 
                to={`/blog/tag/${encodedTag}`}
              >
                {tag}
              </TagItem>
            );
          })}
        </TagsCloud>
      </SidebarWidget>
      
      <SidebarWidget>
        <RSSFeeds />
      </SidebarWidget>
    </Sidebar>
  );
};

export default BlogSidebar; 